import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, LoadingList, SectionHeader } from '@timely/ui';
import { holds } from '@timely/domain';
import { useActiveWorkspace, useWorkspaces } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { Field, FormNotice } from './AuthLayout';
import { supabase } from '../lib/supabaseClient';
import { CURRENCY_CHOICES, LOCALE_CHOICES, TIMEZONE_CHOICES } from '../config/defaults';
import { writeFailure } from '../team/useTeam';
import { CompanyMark } from '../branding/CompanyMark';
import { LogoError, removeLogo, uploadLogo, useLogoUrl } from '../branding/useLogo';

/**
 * What a company records about itself.
 *
 * Two rows, deliberately: the things the product *computes* with — currency,
 * locale, timezone — live on `workspaces` and are read by every screen that
 * formats a number or a date; the things a company merely *states* about itself
 * live on `workspace_settings`.
 *
 * Nothing here assumes a country. The tax identifier is a neutral field with
 * the company's own label beside it, because India's GSTIN, the UK's VAT
 * number and Australia's ABN are the same column with a different word in front
 * of it — and a product that hard-codes one is a product the other two cannot
 * use. The defaults in `config/defaults.ts` are defaults and nothing more.
 */

interface CompanyRow {
    name: string;
    country: string;
    currency: string;
    timezone: string;
    locale: string;
    phone_country: string;
    logo_path: string | null;
    updated_at: string;
}

interface SettingsRow {
    legal_name: string;
    tax_id: string;
    tax_id_label: string;
    secondary_tax_id: string;
    secondary_tax_id_label: string;
    address: string;
    city: string;
    state: string;
    postal_code: string;
    contact_email: string;
    contact_phone: string;
    footer_note: string;
    updated_at: string;
}

const EMPTY_SETTINGS: SettingsRow = {
    legal_name: '', tax_id: '', tax_id_label: 'Tax ID',
    secondary_tax_id: '', secondary_tax_id_label: '',
    address: '', city: '', state: '', postal_code: '',
    contact_email: '', contact_phone: '', footer_note: '', updated_at: '',
};

export function Settings() {
    const context = useActiveWorkspace();
    const workspaceId = context.workspace.id;
    const canEdit = holds(context, 'settings.manage');
    const { reload: reloadWorkspaces } = useWorkspaces();

    const [company, setCompany] = useState<CompanyRow | null>(null);
    const [settings, setSettings] = useState<SettingsRow>(EMPTY_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

    /**
     * What the rows said when this form was filled in.
     *
     * Kept so a save can refuse when somebody else has changed them since —
     * last-write-wins on a settings form is how one person's currency change
     * silently undoes another's address correction.
     */
    const loadedAt = useRef<{ company: string; settings: string }>({ company: '', settings: '' });
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        const client = supabase();
        const [companyResult, settingsResult] = await Promise.all([
            client.from('workspaces')
                .select('name, country, currency, timezone, locale, phone_country, logo_path, updated_at')
                .eq('id', workspaceId).maybeSingle(),
            client.from('workspace_settings')
                .select('legal_name, tax_id, tax_id_label, secondary_tax_id, secondary_tax_id_label, address, city, state, postal_code, contact_email, contact_phone, footer_note, updated_at')
                .eq('workspace_id', workspaceId).maybeSingle(),
        ]);

        // A response that arrives after the company changed belongs to the
        // company it was issued for, not to the one on screen.
        if (issuedFor.current !== workspaceId) return;

        if (companyResult.error || !companyResult.data) {
            setNotice({ tone: 'error', text: 'We could not load your company’s details. Try again in a moment.' });
            setLoading(false);
            return;
        }

        const companyRow = companyResult.data as unknown as CompanyRow;
        const settingsRow = (settingsResult.data as unknown as SettingsRow) ?? EMPTY_SETTINGS;
        setCompany(companyRow);
        setSettings(settingsRow);
        loadedAt.current = { company: companyRow.updated_at, settings: settingsRow.updated_at };
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);

    const save = async () => {
        if (!company) return;
        setSaving(true);
        setNotice(null);
        const client = supabase();

        // Both writes carry the timestamp the form was filled in against, so a
        // row somebody else has touched since matches nothing and is reported
        // rather than overwritten.
        const [companyWrite, settingsWrite] = await Promise.all([
            client.from('workspaces')
                .update({
                    name: company.name.trim(),
                    country: company.country.trim().toUpperCase(),
                    currency: company.currency,
                    timezone: company.timezone,
                    locale: company.locale,
                    phone_country: company.phone_country.replace(/[^0-9]/g, ''),
                })
                .eq('id', workspaceId)
                .eq('updated_at', loadedAt.current.company)
                .select('updated_at'),
            client.from('workspace_settings')
                .update({ ...settings, updated_at: undefined })
                .eq('workspace_id', workspaceId)
                .eq('updated_at', loadedAt.current.settings)
                .select('updated_at'),
        ]);

        setSaving(false);

        if (companyWrite.error || settingsWrite.error) {
            setNotice({
                tone: 'error',
                text: writeFailure(companyWrite.error?.message ?? settingsWrite.error?.message),
            });
            return;
        }

        const changedUnderneath =
            (companyWrite.data ?? []).length === 0 || (settingsWrite.data ?? []).length === 0;

        if (changedUnderneath) {
            setNotice({
                tone: 'error',
                text: 'Somebody else changed these settings while this page was open. Your changes were not saved — reload and make them again.',
            });
            await load();
            return;
        }

        await load();
        setNotice({ tone: 'ok', text: 'Saved.' });
    };

    if (loading || !company) {
        return (
            <Page>
                <SectionHeader title="Company settings" />
                <Card className="mt-5 overflow-hidden"><LoadingList label="Loading your settings" rows={5} /></Card>
            </Page>
        );
    }

    const set = <K extends keyof SettingsRow>(key: K, value: SettingsRow[K]) =>
        setSettings(previous => ({ ...previous, [key]: value }));

    return (
        <Page>
            <SectionHeader
                title="Company settings"
                subtitle="How this company is named, and how it counts money and time."
                actions={canEdit
                    ? <Button variant="primary" disabled={saving} onClick={() => { void save(); }}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                    : undefined}
            />

            <p className="text-[13px] text-label-3 mt-2">
                <Link to={`/w/${context.workspace.slug}/setup`} className="text-accent hover:underline">
                    See company setup status
                </Link>
                {' '}— what is connected, what needs attention, what is still optional.
            </p>

            {notice && <div className="mt-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}

            {!canEdit && (
                <div className="mt-4">
                    <FormNotice tone="error">
                        You can see these settings but not change them. Somebody with the right
                        permission can.
                    </FormNotice>
                </div>
            )}

            <fieldset disabled={!canEdit || saving}>
                <Card className="mt-5 px-5 py-5">
                    <h3 className="text-[15px] font-bold text-label mb-4">The company</h3>

                    <Field
                        label="Name" name="company-name" value={company.name}
                        hint="What this company is called inside Timely Payment."
                        onChange={e => setCompany({ ...company, name: e.target.value })}
                    />
                    <Field
                        label="Registered name" name="legal-name" value={settings.legal_name}
                        hint="If it differs from the name above — used on statements."
                        onChange={e => set('legal_name', e.target.value)}
                    />

                    <div className="grid gap-x-4 sm:grid-cols-2">
                        <Field
                            label="Identifier label" name="tax-label" value={settings.tax_id_label}
                            hint="What your country calls it: VAT number, GSTIN, ABN, EIN."
                            onChange={e => set('tax_id_label', e.target.value)}
                        />
                        <Field
                            label={settings.tax_id_label || 'Tax ID'} name="tax-id" value={settings.tax_id}
                            onChange={e => set('tax_id', e.target.value)}
                        />
                        <Field
                            label="Second identifier label" name="tax2-label"
                            value={settings.secondary_tax_id_label}
                            hint="Leave blank if there is only one."
                            onChange={e => set('secondary_tax_id_label', e.target.value)}
                        />
                        <Field
                            label={settings.secondary_tax_id_label || 'Second identifier'}
                            name="tax2-id" value={settings.secondary_tax_id}
                            onChange={e => set('secondary_tax_id', e.target.value)}
                        />
                    </div>
                </Card>

                <LogoCard company={company} setCompany={setCompany} onChanged={() => { void reloadWorkspaces(); }} />

                <Card className="mt-5 px-5 py-5">
                    <h3 className="text-[15px] font-bold text-label mb-1">Money, dates and language</h3>
                    <p className="text-[13px] text-label-3 mb-4 leading-relaxed">
                        Every amount, date and phone number in this company is formatted from these.
                        They are set per company, so two companies on Timely Payment can work in
                        different currencies without either seeing the other’s.
                    </p>

                    <div className="grid gap-x-4 sm:grid-cols-2">
                        <Choice
                            label="Currency" name="currency" value={company.currency}
                            options={[...CURRENCY_CHOICES]}
                            onChange={v => setCompany({ ...company, currency: v })}
                        />
                        <Choice
                            label="Time zone" name="timezone" value={company.timezone}
                            options={[...TIMEZONE_CHOICES]}
                            hint="Reminder emails are sent by this clock."
                            onChange={v => setCompany({ ...company, timezone: v })}
                        />
                        <Choice
                            label="Number and date format" name="locale" value={company.locale}
                            options={[...LOCALE_CHOICES]}
                            onChange={v => setCompany({ ...company, locale: v })}
                        />
                        <Field
                            label="Phone country code" name="phone-country" value={company.phone_country}
                            inputMode="numeric" hint="Digits only, without the plus."
                            onChange={e => setCompany({ ...company, phone_country: e.target.value })}
                        />
                    </div>
                </Card>

                <Card className="mt-5 px-5 py-5">
                    <h3 className="text-[15px] font-bold text-label mb-4">Where you are, and who to reply to</h3>

                    <Field label="Address" name="address" value={settings.address}
                        onChange={e => set('address', e.target.value)} />
                    <div className="grid gap-x-4 sm:grid-cols-3">
                        <Field label="City" name="city" value={settings.city}
                            onChange={e => set('city', e.target.value)} />
                        <Field label="State or region" name="state" value={settings.state}
                            onChange={e => set('state', e.target.value)} />
                        <Field label="Post code" name="postal-code" value={settings.postal_code}
                            onChange={e => set('postal_code', e.target.value)} />
                    </div>
                    <div className="grid gap-x-4 sm:grid-cols-2">
                        <Field label="Contact email" name="contact-email" type="email"
                            value={settings.contact_email}
                            hint="Shown on statements as the address to reply to."
                            onChange={e => set('contact_email', e.target.value)} />
                        <Field label="Contact phone" name="contact-phone" value={settings.contact_phone}
                            onChange={e => set('contact_phone', e.target.value)} />
                    </div>
                    <Field label="Note at the foot of statements" name="footer-note"
                        value={settings.footer_note}
                        hint="Optional. Bank details, a payment reference, or nothing at all."
                        onChange={e => set('footer_note', e.target.value)} />
                </Card>
            </fieldset>
        </Page>
    );
}

function Choice({
    label, name, value, options, hint, onChange,
}: {
    label: string; name: string; value: string;
    options: string[]; hint?: string;
    onChange: (value: string) => void;
}) {
    return (
        <div className="mb-4">
            <label htmlFor={name} className="block text-[13px] font-semibold text-label mb-1.5">{label}</label>
            <select
                id={name} value={value} onChange={e => onChange(e.target.value)}
                className="w-full h-11 px-3.5 rounded-[11px] bg-card border border-separator-strong text-[15px] text-label outline-none focus:border-accent disabled:opacity-50"
            >
                {/* A value already saved that is not in the short list stays
                    selectable rather than silently changing on the next save. */}
                {!options.includes(value) && <option value={value}>{value}</option>}
                {options.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
            {hint && <div className="text-[12.5px] text-label-3 mt-1.5 leading-snug">{hint}</div>}
        </div>
    );
}

/**
 * The logo, uploaded or removed on its own — never part of the batched save
 * above, and never able to break the rest of the shell if it fails: a bad
 * upload leaves the previous logo (or the initials fallback) exactly as it
 * was, reported as a notice rather than a blank header.
 */
function LogoCard({
    company, setCompany, onChanged,
}: {
    company: CompanyRow;
    setCompany: (next: CompanyRow) => void;
    onChanged: () => void;
}) {
    const context = useActiveWorkspace();
    const workspaceId = context.workspace.id;
    const url = useLogoUrl(company.logo_path);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

    const onFile = async (file: File | undefined) => {
        if (!file) return;
        setBusy(true);
        setNotice(null);
        try {
            const path = await uploadLogo(workspaceId, company.logo_path, file);
            setCompany({ ...company, logo_path: path });
            onChanged();
            setNotice({ tone: 'ok', text: 'Logo updated.' });
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof LogoError ? err.message : 'That did not work.' });
        } finally {
            setBusy(false);
        }
    };

    const onRemove = async () => {
        if (!company.logo_path) return;
        setBusy(true);
        setNotice(null);
        try {
            await removeLogo(workspaceId, company.logo_path);
            setCompany({ ...company, logo_path: null });
            onChanged();
            setNotice({ tone: 'ok', text: 'Logo removed.' });
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof LogoError ? err.message : 'That did not work.' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="mt-5 px-5 py-5">
            <h3 className="text-[15px] font-bold text-label mb-1">Company logo</h3>
            <p className="text-[13px] text-label-3 mb-4 leading-relaxed">
                Shown beside your company name. Optional — without one, your company's initials are
                shown instead. PNG, JPEG or WebP, up to 512 KB.
            </p>

            {notice && <div className="mb-3"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}

            <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-[12px] overflow-hidden bg-card-3 grid place-items-center flex-none">
                    {company.logo_path && url
                        ? <img src={url} alt="Company logo" className="w-full h-full object-cover" />
                        : <CompanyMark name={company.name} logoPath={null} />}
                </div>
                <div className="flex gap-2">
                    <label className="inline-flex">
                        <input
                            type="file" accept="image/png,image/jpeg,image/webp" className="sr-only"
                            disabled={busy}
                            onChange={e => { void onFile(e.target.files?.[0]); e.target.value = ''; }}
                        />
                        <span className="inline-flex items-center justify-center h-9 px-4 rounded-full text-[13.5px] font-semibold bg-card-3 text-label cursor-pointer">
                            {busy ? 'Working…' : company.logo_path ? 'Replace' : 'Upload'}
                        </span>
                    </label>
                    {company.logo_path && (
                        <Button variant="secondary" size="sm" disabled={busy} onClick={() => { void onRemove(); }}>
                            Remove
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
}
